import { useCallback, useEffect, useState } from "react";
import { Close, History, SearchIcon, Send2 } from "../../components/icons/Icon";
import { CloseIcon } from "../../components/icons/Icon2";
import CheckBox from "../../components/updatedComponents/FormComponents/CheckBox";
import styles from "./Marketing2.module.css";
import fetchData from "../../APIsControll/apiControll";
import useDebounce from "../../hooks/useDebounce";
import Button from "../../components/updatedComponents/Dealers/Button";
import { useToast } from "../../components/updatedComponents/Toaster/Toaster";
import Skeleton from "../../components/updatedComponents/Skeleton/Skeleton";
import TextElement from "../../components/updatedComponents/FormComponents/TextElement";
import { getStatusColor } from "../../Utils/Utils";
import LeadHistoryModal from "./LeadHistoryModal";

const ShareLeadModal = ({
  mode,
  closeModal,
  managerId,
  leadId,
  lead,
  getLeads,
  setModalContent,
}) => {
  const { show } = useToast();
  const [searchDealer, setSearchDealer] = useState("");
  const [dealerLoading, setDealerLoading] = useState(false);
  const [dealerResults, setDealerResults] = useState([]);
  const [selectedDealers, setSelectedDealers] = useState([]);
  const [loading, setLoading] = useState(false);

  const debouncedKeyword = useDebounce(searchDealer, 700);

  const fetchDealer = useCallback(async () => {
    if (!debouncedKeyword) {
      setDealerResults([]);
      return;
    }

    setDealerLoading(true);
    try {
      const apiData = {
        managerId: 1,
        keyword: debouncedKeyword,
      };

      const result = await fetchData("order/finddealer", apiData);

      if (result.status === "success") {
        setDealerResults(result.data);
      } else {
        setDealerResults([]);
      }
    } catch (error) {
      console.error("Failed to fetch dealers:", error);
      setDealerResults([]);
    } finally {
      setDealerLoading(false);
    }
  }, [debouncedKeyword]);

  const toggleDealerSelection = (dealer) => {
    setSelectedDealers((prevSelected) => {
      const exists = prevSelected.some((d) => d.dealerId === dealer.dealerId);
      if (exists) {
        return prevSelected.filter((d) => d.dealerId !== dealer.dealerId);
      } else {
        return [...prevSelected, dealer];
      }
    });
  };

  const shareLead = async () => {
    try {
      const apiData = {
        managerId: managerId,
        leadId: leadId,
        dealerIds: selectedDealers.map((d) => d.dealerId),
      };
      setLoading(true);
      const result = await fetchData("leads/shareLead", apiData);

      if (result.status === "success") {
        show(result.msg, "success");
        setLoading(false);
        getLeads();
        closeModal();
      }

      if (result.status === "failed") {
        setLoading(false);
        show(result.msg, "error");
      }

      if (result.status === "warning") {
        setLoading(false);
        show(result.msg, "warning");
      }
    } catch (error) {
      setLoading(false);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDealer();
  }, [fetchDealer]);

  const openShareHistoryModal = () => {
    setModalContent(
      <LeadHistoryModal
        mode={mode}
        closeModal={closeModal}
        managerId={managerId}
        lead={lead}
      />
    );
  };

  return (
    <div
      className={styles.share_lead_modal}
      style={{ backgroundColor: mode && "#232529" }}
    >
      <div className={styles.modal_header}>
        <p style={{ color: mode && "#FFFFFF" }}>Share Lead</p>
        <div onClick={closeModal}>
          <CloseIcon mode={mode} />
        </div>
      </div>

      <div
        className={styles.text_con}
        style={{ borderColor: mode && "#646B88" }}
      >
        <div className={styles.text_con_head}>
          <TextElement
            label={"Name:"}
            labelColor={mode ? "#fff" : "#646B88"}
            value={lead.name}
            valueColor={mode ? "#fff" : "#646B88"}
            fontSize={"14px"}
            fontWeight={"600"}
          />
          <TextElement
            label={"Lead ID:"}
            labelColor={mode ? "#fff" : "#646B88"}
            value={lead.leadId}
            valueColor={mode ? "#fff" : "#646B88"}
            fontSize={"14px"}
            fontWeight={"600"}
          />
        </div>
        <div className={styles.text_con_head}>
          <TextElement
            label={"Mobile:"}
            labelColor={mode ? "#fff" : "#646B88"}
            value={lead.mobile}
            valueColor={mode ? "#fff" : "#646B88"}
            fontSize={"14px"}
            fontWeight={"600"}
          />
          <p
            className={styles.status_text}
            style={{ color: getStatusColor(lead.status) }}
          >
            {lead.status}
          </p>
        </div>
        {lead.type === "wholesale" && (
          <TextElement
            label={"Company:"}
            labelColor={mode ? "#fff" : "#646B88"}
            value={lead.storeName}
            valueColor={mode ? "#fff" : "#646B88"}
            fontSize={"14px"}
            fontWeight={"600"}
          />
        )}
        <TextElement
          label={"Address:"}
          labelColor={mode ? "#fff" : "#646B88"}
          value={
            lead.pincode +
            " " +
            lead.city +
            " " +
            (lead.district || "") +
            " " +
            lead.state
          }
          valueColor={mode ? "#fff" : "#646B88"}
          fontSize={"14px"}
          fontWeight={"600"}
        />
        <TextElement
          label={"Assigned to:"}
          labelColor={mode ? "#fff" : "#646B88"}
          value={lead.assignedManager}
          valueColor={mode ? "#fff" : "#646B88"}
          fontSize={"14px"}
          fontWeight={"600"}
        />
      </div>

      <div
        className={styles.inp}
        style={{
          backgroundColor: mode && "#1B1D21",
          borderColor: mode && "#1B1D21",
        }}
      >
        <SearchIcon color={mode ? "#FFFFFF" : "#646B88"} />
        <input
          type="text"
          placeholder="Search Dealer"
          autoFocus={true}
          value={searchDealer}
          style={{
            backgroundColor: mode && "#1B1D21",
            color: mode && "#FFFFFF",
          }}
          onChange={(e) => setSearchDealer(e.target.value)}
        />
      </div>

      <div className={styles.search_results}>
        {dealerLoading
          ? Array.from({ length: 5 }).map((_, idx) => (
              <div key={idx} className={styles.skeleton_con}>
                <Skeleton lines={1} thickness={4.5} />
              </div>
            ))
          : dealerResults.map((dealer, index) => {
              const isSelected = selectedDealers.some(
                (d) => d.dealerId === dealer.dealerId
              );
              return (
                <div
                  key={dealer.dealerId || index}
                  className={styles.result}
                  onClick={() => toggleDealerSelection(dealer)}
                  style={{
                    backgroundColor: mode && "#232529",
                    cursor: "pointer",
                  }}
                >
                  <CheckBox
                    checked={isSelected}
                    handleChange={() => toggleDealerSelection(dealer)}
                  />
                  <p style={{ color: mode && "#FFFFFF" }}>
                    {dealer.name + " / " + dealer.storeName}
                  </p>
                </div>
              );
            })}

        {debouncedKeyword && !dealerLoading && dealerResults.length === 0 && (
          <p
            style={{
              color: mode && "#FFFFFF",
              marginTop: "auto",
              marginBottom: "auto",
            }}
            className={styles.no_dealer_selected}
          >
            No dealer found!
          </p>
        )}
      </div>

      <div className={styles.selected_optns}>
        {selectedDealers.map((d, index) => (
          <div
            className={styles.selected_option}
            style={{
              backgroundColor: mode && "#1B1D21",
              color: mode && "#FFFFFF",
            }}
            key={d.dealerId + index}
          >
            <div
              className={styles.remove}
              onClick={() =>
                setSelectedDealers((prev) =>
                  prev.filter((dealer) => dealer.dealerId !== d.dealerId)
                )
              }
            >
              <CloseIcon mode={mode} />
            </div>
            <p>{d.name}</p>
            <p>{d.storeName}</p>
            <p>{d.mobile ? d.mobile : d.whatsapp}</p>
            <p>
              {d.pincode +
                ", " +
                d.street +
                ", " +
                d.city +
                ", " +
                d.district +
                ", " +
                d.state}
            </p>
          </div>
        ))}
      </div>

      {selectedDealers.length === 0 && searchDealer.length !== 0 && (
        <p
          style={{
            color: mode && "#FFFFFF",
          }}
          className={styles.no_dealer_selected}
        >
          No dealer selected
        </p>
      )}

      <div className={styles.bottom_buttons}>
        <div style={{ width: "30%" }}>
          <Button
            buttonBgColor={"#000000"}
            buttonTextColor={"#FFFFFF"}
            buttonText={"Close"}
            buttonIcon={<Close />}
            buttonClick={closeModal}
          />
        </div>
        {lead.status === "Sent" && (
          <div style={{ width: "30%" }}>
            <Button
              buttonBgColor={"#4164E3"}
              buttonTextColor={"#FFFFFF"}
              buttonText={"History"}
              buttonIcon={<History />}
              buttonClick={openShareHistoryModal}
            />
          </div>
        )}
        <div style={{ width: "30%" }}>
          <Button
            buttonIcon={<Send2 />}
            buttonText={"Share"}
            buttonBgColor={"#4164E3"}
            buttonTextColor={"#ffffff"}
            buttonBorder={"none"}
            disabled={selectedDealers.length === 0 || loading}
            loading={loading}
            buttonClick={shareLead}
          />
        </div>
      </div>
    </div>
  );
};

export default ShareLeadModal;
